import React from 'react';
import useGameStore from '../../store/gameStore';
import { getSecteur, getModele, computeBfr, TASCOM_SEUIL_M2 } from '../../utils/retail';
import { formatMoney } from '../../utils/format';
import { Receipt } from 'lucide-react';

/**
 * SCD Lot 2 — Carte « Fiscalité du magasin » (affichée en tête de la page
 * Fiscalité) : TVA multi-taux, BFR, bail commercial, TASCOM, démarque,
 * frais CB, accises/licence (caviste), redevances franchise.
 */
export default function RetailFiscalCard() {
  const retail = useGameStore(s => s.retail);
  if (!retail) return null;
  const sec = getSecteur(retail.secteur);
  const mod = getModele(retail.modele);
  const f = retail.fiscal || {};
  const st = retail.stats || {};
  const bfr = computeBfr(retail);
  const collectee = Object.entries(f.tvaMoisCollectee || {});
  const totalCollectee = collectee.reduce((a, [, v]) => a + v, 0);
  const estCaviste = retail.secteur === 'caviste';
  const tascomApplicable = (retail.magasin?.surface || 0) > TASCOM_SEUIL_M2;

  return (
    <div className="surface-card overflow-hidden" data-testid="retail-fiscal-card">
      <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase flex items-center gap-2">
          <Receipt className="w-4 h-4 text-accent" /> Fiscalité du magasin — {sec?.nom}
        </h3>
        <span className="text-[10px] text-slate-500 font-mono">Bail : {formatMoney(retail.magasin?.loyerMensuel || 0)}/mois · dépôt {formatMoney(f.depotGarantie || 0)}</span>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
        <div data-testid="tva-multitaux">
          <p className="font-bold text-slate-300 uppercase tracking-wider text-[10px] mb-1.5">TVA multi-taux (mois en cours)</p>
          {collectee.length === 0 && <p className="text-slate-500">Aucune vente ce mois-ci.</p>}
          {collectee.map(([taux, m]) => (
            <p key={taux} className="font-mono text-slate-300">Taux {taux} % : {formatMoney(Math.round(m))} collectés</p>
          ))}
          <p className="font-mono text-slate-400 mt-1">Déductible (achats) : {formatMoney(Math.round(f.tvaMoisDeductible || 0))}</p>
          <p className="font-mono text-slate-200 font-bold">Nette à reverser : {formatMoney(Math.max(0, Math.round(totalCollectee - (f.tvaMoisDeductible || 0))))}</p>
          <p className="text-[10px] text-slate-500 mt-1">Dernier reversement : {formatMoney(f.tvaDernierReversement || 0)} · cumul {formatMoney(f.tvaVerseeCumul || 0)}</p>
        </div>
        <div data-testid="bfr-bloc">
          <p className="font-bold text-slate-300 uppercase tracking-wider text-[10px] mb-1.5">BFR (stock vs crédit fournisseur)</p>
          <p className="font-mono text-slate-300">Stock valorisé : {formatMoney(bfr.stock)}</p>
          <p className="font-mono text-slate-300">Dettes fournisseurs : −{formatMoney(bfr.dettes)}</p>
          <p className={`font-mono font-bold ${bfr.bfr > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>BFR : {formatMoney(bfr.bfr)}</p>
          <p className="text-[10px] text-slate-500 mt-1">Un BFR positif immobilise votre trésorerie ; le crédit fournisseur (30/60 j) le réduit.</p>
          <p className="font-mono text-slate-400 mt-2">Démarque (charge, cumul) : {formatMoney(st.demarqueEurosCumul || 0)}</p>
          <p className="font-mono text-slate-400">Frais CB (1,2 % × 60 % CA) : {formatMoney(f.fraisCbCumul || 0)}</p>
        </div>
        <div data-testid="taxes-bloc">
          <p className="font-bold text-slate-300 uppercase tracking-wider text-[10px] mb-1.5">Taxes & spécificités</p>
          <p className="font-mono text-slate-300">TASCOM : {tascomApplicable ? `${formatMoney(f.tascomCumul || 0)} (surface > ${TASCOM_SEUIL_M2} m², au 1er janv.)` : `non applicable (≤ ${TASCOM_SEUIL_M2} m²)`}</p>
          <p className="font-mono text-slate-300">Masse salariale (cumul) : {formatMoney(f.masseSalarialeCumul || 0)}</p>
          {estCaviste && (
            <>
              <p className="font-mono text-slate-300 mt-1">Licence alcool : {formatMoney(f.licenceAlcool || 0)} (payée à l'ouverture)</p>
              <p className="font-mono text-slate-300">Droits d'accises (cumul) : {formatMoney(f.accisesCumul || 0)}</p>
              <p className="font-mono text-slate-300">Stockage en cave : {formatMoney(sec?.stockage_cave_mensuel || 0)}/mois</p>
            </>
          )}
          {(mod?.redevance_pct || 0) > 0 && (
            <>
              <p className="font-mono text-slate-300 mt-1">Redevance franchise : {mod.redevance_pct} % CA (cumul {formatMoney(st.redevanceCumul || 0)})</p>
              <p className="font-mono text-slate-300">Redevance pub : {mod.redevance_pub_pct} % CA (cumul {formatMoney(f.redevancePubCumul || 0)})</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
